import React, { ComponentType, useCallback, useRef } from 'react';
import { useExtendableListPagination } from '@cp/shared/pagination';
import { Box, BoxProps } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { styled } from '@cp/ds';
import { ArrowLeftIcon, ArrowRightIcon } from '@cp/ds/src/icons';
import SwiperInstance from 'swiper';
import { CATALOG_FEEDBACK_PAGINATION } from '@common/const/catalog/feedback';
import { Schema } from '@cp/ds/src/components/schema';
import { buildFeedbacksSchema } from '@cp/entities/feedback/lib';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { Button } from '@cp/ds/src/components/button';
import { useIsMobile } from '@cp/ds/src/hooks';
import { useCurrentCity } from '@cp/entities/location';
import { ROUTES_DEFAULT_CITY_ALIAS } from '@common/const/routes/defaultCity';
import { coreCatalogFeedbacksApi } from '@cp/entities/catalog';
import { SwiperSlider } from 'src/app/shared/components/swiper';
import { FeedbackCard } from 'src/widgets/catalog/feedback/feedbacksSlider/components';
import { sliderConfig } from 'src/app/routes/home/const';
import { RoutedLink } from 'src/shared/ui/link';
import { useTrackEvent } from 'src/shared/tracking';
import { createEventFactory } from '@cp/shared/tracking';
import { CatalogFeedbacksProps } from './types';

const MAX_SLIDES_COUNT = 4;

const SliderWrapper = styled(Box, { name: 'SliderWrapper' })(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '20px',
  paddingRight: theme.spacing(0),
  paddingLeft: theme.spacing(1.5),
  minHeight: '300px',
  maxHeight: '500px',
  overflow: 'hidden',
  [theme.breakpoints.up('lg')]: {
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
})) as unknown as ComponentType<BoxProps>;

const ArrowButton = styled(Box, { name: 'ArrowBox' })(({ theme }) => ({
  position: 'relative',
  minWidth: '56px',
  height: '56px',
  color: theme.palette['brand gray'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '12px',
  border: `1px solid ${theme.palette['gray light']}`,
  backgroundColor: theme.palette.white,
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
})) as unknown as ComponentType<BoxProps>;

export const Feedbacks: React.FC<CatalogFeedbacksProps> = ({ catalogType, careerId, cityId, productName, withBorder }) => {
  const swiperRef = useRef<SwiperInstance>(null);
  const isMobile = useIsMobile();
  const track = useTrackEvent();
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const babysittersHref =
    currentCity?.alias && currentCity.alias !== ROUTES_DEFAULT_CITY_ALIAS
      ? `/${currentCity.alias}/babysitters/`
      : '/babysitters/';

  const { items, isLoading, additionalData, onLoadMore, canLoadMore } = useExtendableListPagination({
    parameters: {
      type: catalogType,
      cityId,
      careerId,
      pagination: 6,
    },
    query: coreCatalogFeedbacksApi.endpoints.getList,
  });

  const { averageRating, totalCount } = additionalData ?? {};
  const limit = 12;

  const handleLoadMore = useCallback(() => {
    if (canLoadMore && items?.length < limit && !isLoading) {
      onLoadMore();
    }
  }, [canLoadMore, isLoading, items?.length, onLoadMore]);

  const handlePrev = useCallback(() => {
    swiperRef.current?.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    swiperRef.current?.slideNext();
    handleLoadMore();
  }, [handleLoadMore]);

  const schema = React.useMemo(() => {
    if (!productName || !averageRating || !items || !totalCount) {
      return;
    }

    return buildFeedbacksSchema({
      productName,
      averageRating,
      totalCount,
      items: items.slice(0, CATALOG_FEEDBACK_PAGINATION),
    });
  }, [items, productName, averageRating, totalCount]);

  return (
    <Stack alignItems="center" sx={{ gap: { xs: '30px', md: '48px' }, pb: { xs: '20px', md: '48px' }, m: '0 auto' }}>
      <SliderWrapper>
        <ArrowButton onClick={handlePrev}>
          <ArrowLeftIcon />
        </ArrowButton>
        <Box sx={{ overflow: 'hidden', maxWidth: '1430px', width: '100%' }}>
          {items?.length ? (
            <SwiperSlider config={sliderConfig} onSlideChange={handleLoadMore} ref={swiperRef}>
              {items.map((item, i) => {
                return (
                  <FeedbackCard feedback={item} key={i} loadPolicy={i <= MAX_SLIDES_COUNT ? 'eager' : 'lazy'} withBorder={withBorder} />
                );
              })}
            </SwiperSlider>
          ) : isLoading ? (
            <ContentSkeleton blocks={2} sx={{ width: 1 }} />
          ) : (
            <Box />
          )}
        </Box>
        <ArrowButton onClick={handleNext}>
          <ArrowRightIcon />
        </ArrowButton>

        {schema && <Schema schema={schema} />}
      </SliderWrapper>
      <RoutedLink
        href={babysittersHref}
        onClick={() => track(createEventFactory({ subject: 'feedbacks-slider', action: 'click', object: 'find-specialist' })())}
        sx={{ display: 'contents' }}
        underline={false}
      >
        <Button as="span" color="pink" size={isMobile ? 'small' : 'normal'}>
          Найти бебиситтера
        </Button>
      </RoutedLink>
    </Stack>
  );
};
